import type { Action } from './reducer';
import type {
  StatefulDirectCall,
  StatefulLocalParticipant,
  StatefulRemoteParticipant,
  StatefulRoom,
} from './types';
import type {
  CustomItems,
  DirectCall,
  LocalParticipant,
  RemoteParticipant,
  Room,
} from 'sendbird-calls';
import { RoomState } from 'sendbird-calls';

// statefyRoom can run multiple times for the same underlying SDK room (e.g. via
// updateRoom()); track which rooms already have the customItemsUpdated listener
// so we register it exactly once and don't stack duplicate dispatches.
const customItemsListenerRooms = new WeakSet<Room>();

const registerDirectCallListeners = (
  call: DirectCall,
  dispatchUpdate: (part: Partial<StatefulDirectCall>) => void
) => {
  call.onEstablished = () => {
    dispatchUpdate({ callState: 'established' });
  };
  call.onConnected = () => {
    dispatchUpdate({ callState: 'connected' });
  };
  call.onReconnected = () => {
    dispatchUpdate({ callState: 'reconnected' });
  };
  call.onReconnecting = () => {
    dispatchUpdate({ callState: 'reconnecting' });
  };
  call.onEnded = () => {
    dispatchUpdate({ callState: 'ended' });
  };
  call.onRemoteAudioSettingsChanged = (call: DirectCall) => {
    dispatchUpdate({ isRemoteAudioEnabled: call.isRemoteAudioEnabled });
  };
  call.onRemoteVideoSettingsChanged = (call: DirectCall) => {
    dispatchUpdate({ isRemoteVideoEnabled: call.isRemoteVideoEnabled });
  };
};

export const statefyRoom = (room: Room, dispatch: React.Dispatch<Action>): StatefulRoom => {
  const dispatchUpdate = (part: Partial<StatefulRoom>) => {
    const payload = {
      roomId: room.roomId,
      ...part,
    };
    dispatch({ type: 'UPDATE_ROOM', payload });
  };

  const updateRoom = () => {
    dispatchUpdate(statefyRoom(room, dispatch));
  };

  const updateLocalParticipant = (participant: Partial<StatefulLocalParticipant>) => {
    dispatch({
      type: 'UPDATE_ROOM_LOCAL_PARTICIPANT',
      payload: {
        roomId: room.roomId,
        participant,
      },
    });
  };
  const upsertRemoteParticipant = (participant: RemoteParticipant) => {
    dispatch({
      type: 'UPSERT_ROOM_REMOTE_PARTICIPANT',
      payload: {
        roomId: room.roomId,
        participant: statefyRemoteParticipant(participant),
      },
    });
  };

  const deleteRemoteParticipant = (participant: RemoteParticipant) => {
    dispatch({
      type: 'DELETE_ROOM_REMOTE_PARTICIPANT',
      payload: {
        roomId: room.roomId,
        participantId: participant.participantId,
      },
    });
  };

  const statefulLocalParticipants = room.localParticipant
    ? [statefyLocalParticipant(room.localParticipant, updateLocalParticipant)]
    : [];
  const statefulRemoteParticipants = room.remoteParticipants.map(statefyRemoteParticipant);
  const statefulParticipants = [...statefulLocalParticipants, ...statefulRemoteParticipants];

  room.on('remoteParticipantEntered', upsertRemoteParticipant);
  room.on('remoteParticipantStreamStarted', upsertRemoteParticipant);
  room.on('remoteParticipantExited', deleteRemoteParticipant);
  room.on('remoteAudioSettingsChanged', upsertRemoteParticipant);
  room.on('remoteVideoSettingsChanged', upsertRemoteParticipant);
  if (!customItemsListenerRooms.has(room)) {
    customItemsListenerRooms.add(room);
    room.on('customItemsUpdated', (customItems) => {
      dispatchUpdate({ customItems });
    });
  }
  room.on('deleted', () => {
    dispatchUpdate({ state: RoomState.DELETED });
  });
  room.on('error', (error) => {
    console.error(error); // TODO:
  });
  return {
    roomId: room.roomId,
    roomType: room.roomType,
    createdAt: room.createdAt,
    createdBy: room.createdBy,
    customItems: room.customItems,
    state: room.state,
    participants: statefulParticipants,
    localParticipant: statefulLocalParticipants[0],
    remoteParticipants: statefulRemoteParticipants,
    async enter(params) {
      await room.enter(params);
      updateRoom();
      return;
    },
    async sendInvitation(invitee: string) {
      const data = await room.sendInvitation(invitee);
      return data;
    },
    async updateCustomItems(items: CustomItems) {
      const result = await room.updateCustomItems(items);
      dispatchUpdate({ customItems: room.customItems });
      return result;
    },
    async exit() {
      try {
        await room.exit();
        // SDK's exit() is sync but internal teardown is not — yield once so it can progress.
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      } catch (err) {
        if (err?.name !== 'NotAllowedError') console.error(err);
      } finally {
        updateRoom();
      }
    },
    async delete() {
      try {
        await room.delete();
        return;
      } catch (err) {
        console.error(err);
      }
    },
  };
};

export const statefyLocalParticipant = (
  participant: LocalParticipant,
  update: (participant: Partial<StatefulLocalParticipant>) => void
): StatefulLocalParticipant => {
  // The SDK's media toggles read this.stream synchronously and throw a TypeError if the
  // local stream was already released (e.g. iOS reclaimed the camera, or the room is
  // tearing down). The SDK flips its own isAudioEnabled/isVideoEnabled only after that
  // read, so on failure we skip our update too and the buttons keep matching the SDK.
  const toggleMedia = (
    name: string,
    toggle: () => void,
    state: Partial<StatefulLocalParticipant>
  ) => {
    try {
      toggle();
      update(state);
    } catch (err) {
      console.warn(`${name} failed`, err);
    }
  };

  return {
    participantId: participant.participantId,
    enteredAt: participant.enteredAt,
    updatedAt: participant.updatedAt,
    exitedAt: participant.exitedAt,
    duration: participant.duration,
    isLocalParticipant: participant.isLocalParticipant,
    state: participant.state,
    user: participant.user, // TODO: Statefy user
    isAudioEnabled: participant.isAudioEnabled,
    isVideoEnabled: participant.isVideoEnabled,
    setMediaView(mediaView: HTMLMediaElement) {
      return participant.setMediaView(mediaView);
    },
    setLocalMediaView(mediaView: HTMLMediaElement) {
      return participant.setLocalMediaView(mediaView);
    },
    muteMicrophone() {
      toggleMedia('muteMicrophone', () => participant.muteMicrophone(), { isAudioEnabled: false });
    },
    unmuteMicrophone() {
      toggleMedia('unmuteMicrophone', () => participant.unmuteMicrophone(), {
        isAudioEnabled: true,
      });
    },
    startVideo() {
      toggleMedia('startVideo', () => participant.startVideo(), { isVideoEnabled: true });
    },
    stopVideo() {
      toggleMedia('stopVideo', () => participant.stopVideo(), { isVideoEnabled: false });
    },
  };
};

export const statefyRemoteParticipant = (
  participant: RemoteParticipant
): StatefulRemoteParticipant => {
  return {
    participantId: participant.participantId,
    enteredAt: participant.enteredAt,
    updatedAt: participant.updatedAt,
    exitedAt: participant.exitedAt,
    duration: participant.duration,
    isLocalParticipant: participant.isLocalParticipant,
    state: participant.state,
    user: participant.user, // TODO: Statefy user
    isAudioEnabled: participant.isAudioEnabled,
    isVideoEnabled: participant.isVideoEnabled,
    setMediaView(mediaView: HTMLMediaElement) {
      return participant.setMediaView(mediaView);
    },
  };
};

export const statefyDirectCall = (
  call: DirectCall,
  dispatch: React.Dispatch<Action>,
  registerListener: boolean
): StatefulDirectCall => {
  const dispatchUpdate = (part: Partial<StatefulDirectCall>) => {
    const payload = {
      callId: call.callId,
      ...part,
    };
    dispatch({ type: 'UPDATE_CALL', payload });
  };

  if (registerListener) {
    registerDirectCallListeners(call, dispatchUpdate);
  }

  return {
    callState: call.localUser.userId === call.caller.userId ? 'dialing' : 'ringing',
    callId: call.callId,
    caller: call.caller, // This should not mutate
    callee: call.callee, // This should not mutate
    isVideoCall: call.isVideoCall,
    localUser: call.localUser, // This should not mutate
    remoteUser: call.remoteUser, // This should not mutate
    isLocalAudioEnabled: call.isLocalAudioEnabled,
    isRemoteAudioEnabled: call.isRemoteAudioEnabled,
    isLocalVideoEnabled: call.isLocalVideoEnabled,
    isRemoteVideoEnabled: call.isRemoteVideoEnabled,
    myRole: call.myRole,
    isOngoing: call.isOngoing,
    endedBy: call.endedBy, // This should not mutate
    isEnded: call.isEnded,
    endResult: call.endResult,
    localMediaView: call.localMediaView,
    remoteMediaView: call.remoteMediaView,

    setLocalMediaView(mediaView) {
      dispatchUpdate({ localMediaView: mediaView });
      return call.setLocalMediaView(mediaView);
    },
    setRemoteMediaView(mediaView) {
      dispatchUpdate({ remoteMediaView: mediaView });
      return call.setRemoteMediaView(mediaView);
    },

    stopVideo() {
      dispatchUpdate({ isLocalVideoEnabled: false });
      return call.stopVideo();
    },
    startVideo() {
      dispatchUpdate({ isLocalVideoEnabled: true });
      return call.startVideo();
    },
    getDuration() {
      return call.getDuration();
    },
    accept(params) {
      return call.accept(params);
    },
    end() {
      return call.end();
    },

    muteMicrophone() {
      dispatchUpdate({ isLocalAudioEnabled: false });
      return call.muteMicrophone();
    },
    unmuteMicrophone() {
      dispatchUpdate({ isLocalAudioEnabled: true });
      return call.unmuteMicrophone();
    },
  };
};
